import { batch } from 'react-redux'
import { customizeError, formatDNI, formatPercentage } from '@chilecompra/react-kit'

import { makeActionCreator } from '../../config/store/utils'
import { checkIfForeignDni, isNaturalPerson } from '../../modules/utils/helpers'
import { getSellerAffidavit } from '../../services/partnerships'
import { getSellerBusiness } from '../../services/sii'
import {
  onGetFinalBeneficiariesAffidavit,
  onGetFinalBeneficiariesAffidavitError,
  onGetFinalBeneficiariesAffidavitSuccess
} from '../FinalBeneficiarySection/FinalBeneficiarySection.actions'
import {
  onGetRelatedBeneficiariesAffidavit,
  onGetRelatedCompanyAffidavitError,
  onGetRelatedCompanyAffidavitSuccess
} from '../RelatedCompanySection/RelatedCompanySection.actions'

export const GET_SELLER_AFFIDAVIT_GRID = 'GET_SELLER_AFFIDAVIT_GRID'
export const GET_SELLER_AFFIDAVIT_GRID_ERROR = 'GET_SELLER_AFFIDAVIT_GRID_ERROR'
export const GET_SELLER_AFFIDAVIT_GRID_SUCCESS = 'GET_SELLER_AFFIDAVIT_GRID_SUCCESS'
export const onGetSellerAffidavitGrid = makeActionCreator(GET_SELLER_AFFIDAVIT_GRID)
export const onGetSellerAffidavitGridError = makeActionCreator(GET_SELLER_AFFIDAVIT_GRID_ERROR, 'payload')
export const onGetSellerAffidavitGridSuccess = makeActionCreator(GET_SELLER_AFFIDAVIT_GRID_SUCCESS, 'payload')
export const onGetSellerAffidavitGridThunk = payload => async dispatch => {
  const { dni } = payload || {}

  dispatch(onGetSellerAffidavitGrid())
  dispatch(onGetFinalBeneficiariesAffidavit())
  dispatch(onGetRelatedBeneficiariesAffidavit())

  try {
    const { data: sellerAffidavitData } = await getSellerAffidavit({ actionType: GET_SELLER_AFFIDAVIT_GRID, dni })
    const { data: sellerBusinessData } = !checkIfForeignDni(dni)
      ? await getSellerBusiness({ dni: formatDNI(dni, 2) })
      : {}

    const hasAffidavit = !!sellerAffidavitData?.payload?.sociosAccionistas
    const isForcedAffidavit = sellerAffidavitData?.payload?.forzado
    const otherPartners = sellerAffidavitData?.payload?.otrosAccionistas
    const taxPayerType = sellerBusinessData?.payload?.tipoContribuyenteCod

    const partnerships =
      sellerAffidavitData.payload?.sociosAccionistas?.map(partnership => {
        const formattedDNI =
          partnership.tipoPersona === 1
            ? formatDNI(`${partnership.rut}-${partnership.digito}`).toUpperCase()
            : partnership.rutExtranjero

        const formattedName = [
          partnership.nombreApellidoRazonSocial,
          partnership.primerApellido !== null && partnership.primerApellido
        ]
          .filter(Boolean)
          .join(' ')
          .replace('null', '')
          .trim()

        const formattedPercentage = formatPercentage(partnership.participacionSobreCapital, 2)

        return {
          dni: formattedDNI,
          isForeign: checkIfForeignDni(formattedDNI),
          isNaturalPerson: isNaturalPerson(formattedDNI),
          name: `${formattedName}`,
          partners: [],
          percentage: formattedPercentage
        }
      }) || []

    const toNumber = v => {
      if (v === null || v === undefined) return 0
      return Number(String(v).replace('%', '').replace(',', '.')) || 0
    }
    const beneficiaries =
      sellerAffidavitData?.payload?.beneficiarios
        ?.filter(beneficiary => toNumber(beneficiary.porcentaje) > 10)
        ?.map(beneficiary => {
          const formattedDNI =
            Number(beneficiary.tipoPersona) === 1 ? formatDNI(beneficiary.rut).toUpperCase() : beneficiary.rut
          const formattedPercentage = formatPercentage(beneficiary.porcentaje, 2)
          return {
            dni: formattedDNI,
            isNaturalPerson: isNaturalPerson(formattedDNI),
            name: beneficiary.nombre,
            percentage: formattedPercentage
          }
        }) || []

    const relatedCompanies =
      sellerAffidavitData?.payload?.empresasRelacionadas?.map(company => {
        const formattedDNI = Number(company.tipoPersona) === 1 ? formatDNI(company.rut).toUpperCase() : company.rut

        const formattedName = company.razonSocial
          ? company.razonSocial
          : [
              company.nombres,
              company.apellidoPaterno !== null && company.apellidoPaterno,
              company.apellidoMaterno !== null && company.apellidoMaterno
            ]
              .filter(Boolean)
              .join(' ')
              .replace('null', '')
              .trim()

        const IDS_DESDE_ADMINISTRADOR = ['9', '10', '11', '12', '13']
        return {
          dni: formattedDNI,
          isFinalBeneficiary: beneficiaries.some(beneficiary => {
            if (beneficiary.dni !== company.rut) return false

            return true
          }),
          isNaturalPerson: isNaturalPerson(formattedDNI),
          name: `${formattedName}`,
          esDesdeAdministrador: IDS_DESDE_ADMINISTRADOR.includes(company.tipoRelacion),
          relation:
            company.tipoRelacion > 5 && !IDS_DESDE_ADMINISTRADOR.includes(company.tipoRelacion)
              ? `Otros - ${company.descTipoRelacion}`
              : company.descTipoRelacion
        }
      }) || []

    return batch(() => {
      dispatch(
        onGetSellerAffidavitGridSuccess({
          hasAffidavit,
          isForcedAffidavit,
          otherPartners:
            otherPartners === null
              ? undefined
              : {
                  numberOfPartners: otherPartners?.cantidadSocios,
                  percentage: formatPercentage(otherPartners?.porcentaje, 2)
                },
          partnerships,
          taxPayerType
        })
      )

      dispatch(onGetFinalBeneficiariesAffidavitSuccess({ beneficiaries, hasAffidavit, isForcedAffidavit }))
      dispatch(onGetRelatedCompanyAffidavitSuccess({ hasAffidavit, isForcedAffidavit, relatedCompanies }))
    })
  } catch (error) {
    let reason

    switch (error.code) {
      case 403:
        reason = 'FORBIDDEN_ERROR'
        break
      default: {
        dispatch(
          onGetFinalBeneficiariesAffidavitError({
            error: customizeError({ originalError: error?.originalError, code: 403, reason }).toObject()
          })
        )
        dispatch(
          onGetRelatedCompanyAffidavitError({
            error: customizeError({ originalError: error?.originalError, code: 403, reason }).toObject()
          })
        )

        return dispatch(onGetSellerAffidavitGridSuccess({ partnerships: [] }))
      }
    }

    return dispatch(
      onGetSellerAffidavitGridError({
        error: customizeError({
          originalError: error?.originalError,
          code: 403,
          reason
        }).toObject()
      })
    )
  }
}
